import { HttpEventType } from '@angular/common/http';
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import moment from 'moment';
import { Popover } from 'primeng/popover';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ClassMaintenanceOrderEnum, MaintenanceOrderEnums, TypeMaintenanceOrderEnum } from 'src/app/enum/maintenance-order-enum';
import { MaintenanceOrderImportModel } from 'src/app/models/imports/maintenance-order-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceOrderService } from 'src/app/services/maintenance/maintenance-order.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-maintenance-order-import',
  templateUrl: './maintenance-order-import.component.html',
  styleUrls: ['./maintenance-order-import.component.scss'],
  standalone: false
})
export class MaintenanceOrderImportComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly maintenanceOrderService = inject(MaintenanceOrderService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('information') informationPopover!: Popover;

  branchCode: number = 0;
  branchId: number = 0;
  companyCode: number = 0;
  companyId: number = 0;
  errorMaintenanceOrder: number = 0;
  eventFile: any;
  formImport: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listCompanies: any[] = [];
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  maintenanceOrdersAlerts: PreInvoicingAlertModel[] = [];
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Ordenes de Mantenimiento';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "maintenance-order-import";
  visibleTxt: boolean = false;

  async ngOnInit() {
    this.validateForm();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    await this.getPermission();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.maintenanceOrdersAlerts || this.maintenanceOrdersAlerts.length === 0) {
      this.toastHandlerService.showToast('error', 'No hay alertas para exportar.');
      return;
    }

    this.maintenanceOrderService.exportImportErrors(this.maintenanceOrdersAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionOrdenesDeMantenimiento_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast('error', 'Error al exportar el archivo');
      }
    });
  }

  downloadGuideImport() {
    this.maintenanceOrderService.downloadGuideImport().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaOrdenesDeMantenimiento.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(this.companyService.getAllCompaniesForSelectAsync());
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listBranches = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    await this.getAllLists('branches');
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('companies'),
      this.getPermissionProfileByUrl(),
    ])
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos.', error);
    }
  }

  importMaintenanceOrders() {
    this.maintenanceOrdersAlerts = [];
    this.errorMaintenanceOrder = 0;
    this.visibleTxt = false;
    
    if (!this.formImport.valid) {
      this.showValidationMessages();
      return;
    }
    let isOk: boolean = true;
    const input = document.getElementById("fileImport") as any;
    const rowsImports = new Array<MaintenanceOrderImportModel>();
    let row: MaintenanceOrderImportModel;
    let counter: number = 1, maintenanceOrderClass: number, maintenanceOrderType: number, maintenanceOrderConsecutive: number,
      maintenanceOrderAlterNumber: number | null, maintenanceOrderDate: Date, workshopType: number, clientDocument: string | null,
      clientDocument2: string | null, costCenterCode: number | null, equipmentCode: number, storeCode: number, mechanicDocument: string, maintenanceType: number,
      initialDate: Date, initialDiagnosis: string | null, finalDate: Date | null, finalDiagnosis: string | null, repairHours: number,
      kmHoromValue: number, stageCode: number, reportedBy: string | null, receivedBy: string | null, contactConstructionSite: string | null,
      dateCompromise: Date | null, transportDocument: string | null, vehicleCode: number | null, transportValue: number,
      transportValueToPay: number, valueToInvoiced: number, invoiceNumber: string | null, swChargeConstructionSite: string,
      costRepairValue: number, swDeliveredOnTime: string | null, qualification: number | null, stateMaintenanceOrder: number;
    if (input.files[0]) {
      readXlsxFile(input.files[0]).then(async (rows) => {
        if (rows.length <= 1) {
          this.toastHandlerService.showToast('warning', `El archivo ingresado no tiene registros para importar`);
          return;
        }
        rows.splice(0, 1);
        rows.forEach(element => {
          counter = counter + 1;
          let rowHasError = false;
          //A. Código Compañía:
          const companyCode = parseFloat(element[0] as string);
          if (isNaN(companyCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna A. El código de la compañía no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (companyCode != this.companyCode) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna A. La compañía del archivo no es igual a la compañía seleccionada.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //B. Código Sucursal:
          const branchCode = parseFloat(element[1] as string);
          if (isNaN(branchCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna B. El código de la sucursal no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (branchCode != this.branchCode) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna B. La sucursal del archivo no es igual a la sucursal seleccionada.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //C. Clase de la Orden de Mantenimiento:
          maintenanceOrderClass = parseFloat(element[2] as string);
          if (isNaN(maintenanceOrderClass)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna C. La clase de la orden de mantenimiento no es válida.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (maintenanceOrderClass != ClassMaintenanceOrderEnum.enTaller && maintenanceOrderClass != ClassMaintenanceOrderEnum.enSitio &&
            maintenanceOrderClass != ClassMaintenanceOrderEnum.programada
          ) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}Columna C La clase de la orden de mantenimiento no es válida.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //D. Tipo de Orden de Mantenimiento:
          maintenanceOrderType = parseFloat(element[3] as string);
          if (isNaN(maintenanceOrderType)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna D. El tipo de orden de mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (maintenanceOrderType != TypeMaintenanceOrderEnum.equipoPropioDeAlmacen && maintenanceOrderType != TypeMaintenanceOrderEnum.equipoDeTerceroEnTaller &&
            maintenanceOrderType != TypeMaintenanceOrderEnum.equipoPropioEnObra && maintenanceOrderType != TypeMaintenanceOrderEnum.equipoClienteEnObra &&
            maintenanceOrderType != TypeMaintenanceOrderEnum.equipoDeTerceroEnCampo && maintenanceOrderType != TypeMaintenanceOrderEnum.mantenimientoProgramado &&
            maintenanceOrderType != TypeMaintenanceOrderEnum.equipoPropioDeObra) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna D. El tipo de orden de mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //E. Número de Orden de Mantenimiento:
          maintenanceOrderConsecutive = parseFloat(element[4] as string);
          if (isNaN(maintenanceOrderConsecutive)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna E. El número de Orden de Mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (maintenanceOrderConsecutive < 1) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna E. El número de Orden de Mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //F. Número Alterno de Orden de Mantenimiento:
          maintenanceOrderAlterNumber = parseFloat(element[5] as string);
          if (maintenanceOrderAlterNumber != null && maintenanceOrderAlterNumber < 1) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna F. El número alterno de la Orden de Mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //G. Fecha:
          const dateMaintenanceOrderString = element[6] as string;
          if (dateMaintenanceOrderString == null || dateMaintenanceOrderString == "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna G. El campo de fecha de la Orden de Mantenimiento es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          //Convierte fecha de string a Date
          maintenanceOrderDate = moment(dateMaintenanceOrderString, 'DD/MM/YYYY HH:mm').toDate();
          if (!moment(dateMaintenanceOrderString, 'DD/MM/YYYY HH:mm').isValid()) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna G. El campo de fecha de la Orden de Mantenimiento no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //H. Tipo Taller:
          workshopType = parseFloat(element[7] as string);
          if (isNaN(workshopType)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna H. El tipo taller no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          if (workshopType != 2 && workshopType != 3) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna H. El tipo taller no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //I. Documento Cliente:
          clientDocument = element[8] as string;
          if (maintenanceOrderType != TypeMaintenanceOrderEnum.equipoPropioDeAlmacen) {
            if (clientDocument == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. El documento del cliente no es válido.`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            } else if (clientDocument != null && clientDocument.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. El documento del cliente supera el tamaño máximo de caracteres (15)`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          }

          //J. Documento Cliente 2:
          clientDocument2 = element[9] as string;
          if (clientDocument2 != null) {
            if (clientDocument2.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna J. El documento del cliente 2 supera el tamaño máximo de caracteres (15)`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          }

          //K. Centro de Costo:
          const costCenterCodeValue = element[10];
          if (costCenterCodeValue == null || costCenterCodeValue === "") {
            costCenterCode = null;
          } else {
            costCenterCode = Number(costCenterCodeValue);
            if (isNaN(costCenterCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna K. El centro de costo no es válido.`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          }

          //L. Código Equipo:
          const valEquipmentCode = element[11];
          if (valEquipmentCode == null || valEquipmentCode === "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna L. El código de equipo es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          equipmentCode = Number(valEquipmentCode);
          if (isNaN(equipmentCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna L. El código de equipo no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //M. Código Bodega:
          const valStoreCode = element[12];
          if (valStoreCode == null || valStoreCode === "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna M. El código de la bodega es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          storeCode = Number(valStoreCode);
          if (isNaN(storeCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna M. El código de la bodega no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //N. Documento Mecánico:
          mechanicDocument = (element[13] != null && element[13] !== "") ? String(element[13]) : "";
          if (!mechanicDocument) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna N. El documento del mecánico es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          } else if (mechanicDocument != null && mechanicDocument.length > 15) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna N. El documento del mecánico supera el tamaño máximo de caracteres (15)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //O. Tipo de Mantenimiento:
          const valMaintenanceType = element[14];
          if (valMaintenanceType == null || valMaintenanceType === "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna O. El tipo de mantenimiento es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          maintenanceType = Number(valMaintenanceType);
          if (isNaN(maintenanceType) || (maintenanceType !== 1 && maintenanceType !== 2 && maintenanceType !== 3)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna O. El tipo de mantenimiento no es válido (debe ser 1, 2 o 3).`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //P. Fecha Inicial:
          const initialDateString = element[15] as string;
          if (initialDateString == null || initialDateString === "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna P. La fecha inicial es obligatoria.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          initialDate = moment(initialDateString, 'DD/MM/YYYY HH:mm').toDate();
          if (!moment(initialDateString, 'DD/MM/YYYY HH:mm').isValid()) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna P. La fecha inicial no es válida.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //Q. Diagnóstico Inicial:
          initialDiagnosis = (element[16] != null && element[16] !== "") ? String(element[16]) : null;

          //R. Fecha Final:
          const finalDateString = element[17] as string;
          if (finalDateString != null && finalDateString != "") {
            finalDate = moment(finalDateString, 'DD/MM/YYYY HH:mm').toDate();
            if (!moment(finalDateString, 'DD/MM/YYYY HH:mm').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna R. La fecha final no es válida.`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          } else {
            finalDate = null;
          }

          //S. Diagnóstico Final:
          finalDiagnosis = (element[18] != null && element[18] !== "") ? String(element[18]) : null;

          //T. Horas Reparación:
          const valRepairHours = element[19];
          repairHours = (valRepairHours == null || valRepairHours === "") ? 0 : Number(valRepairHours);
          if (isNaN(repairHours)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna T. Las horas de reparación no son válidas.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //U. Valor Horómetro KM:
          const valKmHoromValue = element[20];
          kmHoromValue = (valKmHoromValue == null || valKmHoromValue === "") ? 0 : Number(valKmHoromValue);
          if (isNaN(kmHoromValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna U. El valor de horómetro/km no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //V. Código Etapa:
          const valStageCode = element[21];
          if (valStageCode == null || valStageCode === "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna V. El código de etapa es obligatorio.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          stageCode = Number(valStageCode);
          if (isNaN(stageCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna V. El código de etapa no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //W. Reportado Por:
          reportedBy = (element[22] != null && element[22] !== "") ? String(element[22]) : null;
          if (reportedBy != null && reportedBy.length > 50) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna W. El campo Reportado por supera el tamaño máximo de caracteres (50)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //X. Recibido Por:
          receivedBy = (element[23] != null && element[23] !== "") ? String(element[23]) : null;
          if (receivedBy != null && receivedBy.length > 50) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna X. El campo Recibido por supera el tamaño máximo de caracteres (50)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //Y. Contacto Obra:
          contactConstructionSite = (element[24] != null && element[24] !== "") ? String(element[24]) : null;
          if (contactConstructionSite != null && contactConstructionSite.length > 50) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna Y. El campo Contacto Obra supera el tamaño máximo de caracteres (50)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //Z. Fecha Compromiso:
          const dateCompromiseString = element[25] as string;
          if (dateCompromiseString) {
            dateCompromise = moment(dateCompromiseString, 'DD/MM/YYYY').toDate();
            if (!moment(dateCompromiseString, 'DD/MM/YYYY').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Z. La fecha de compromiso no es válida.`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          } else {
            dateCompromise = null;
          }

          //AA. Documento Conductor:
          transportDocument = (element[26] != null && element[26] !== "") ? String(element[26]) : null;
          if (transportDocument != null && transportDocument.length > 15) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AA. El campo Documento Conductor supera el tamaño máximo de caracteres (15)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AB. Código Vehículo:
          const valVehicleCode = element[27];
          if (valVehicleCode == null || valVehicleCode === "") {
            vehicleCode = null;
          } else {
            vehicleCode = Number(valVehicleCode);
            if (isNaN(vehicleCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AB. El código de vehículo no es válido.`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          }

          //AC. Valor Transporte:
          const valTransportValue = element[28];
          transportValue = (valTransportValue == null || valTransportValue === "") ? 0 : Number(valTransportValue);
          if (isNaN(transportValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AC. El valor de transporte no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AD. Valor Transporte a Pagar:
          const valTransportValueToPay = element[29];
          transportValueToPay = (valTransportValueToPay == null || valTransportValueToPay === "") ? 0 : Number(valTransportValueToPay);
          if (isNaN(transportValueToPay)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AD. El valor de transporte a pagar no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AE. Valor a Facturar:
          const valValueToInvoiced = element[30];
          valueToInvoiced = (valValueToInvoiced == null || valValueToInvoiced === "") ? 0 : Number(valValueToInvoiced);
          if (isNaN(valueToInvoiced)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AE. El valor a facturar no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AF. Número Factura:
          invoiceNumber = (element[31] != null && element[31] !== "") ? String(element[31]) : null;
          if (invoiceNumber != null && invoiceNumber.length > 50) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AF. El número de factura supera el tamaño máximo de caracteres (50)`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AG. Cobro Obra:
          const valSwCharge = element[32] ? String(element[32]).toUpperCase() : null;
          if (valSwCharge !== null && valSwCharge !== "S" && valSwCharge !== "N" && valSwCharge !== "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AG. El cobro a obra no es válido (S, N o vacío).`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          swChargeConstructionSite = (valSwCharge === "S" || valSwCharge === "N") ? valSwCharge : "N";

          //AH. Valor Costo Reparación:
          const valCostRepair = element[33];
          costRepairValue = (valCostRepair == null || valCostRepair === "") ? 0 : Number(valCostRepair);
          if (isNaN(costRepairValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AH. El valor costo reparación no es válido.`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          //AI. Entregado a Tiempo:
          const valSwDelivered = element[34] ? String(element[34]).toUpperCase() : null;
          if (valSwDelivered !== null && valSwDelivered !== "S" && valSwDelivered !== "N" && valSwDelivered !== "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AI. Entregado a tiempo no es válido (S, N o vacío).`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }
          swDeliveredOnTime = (valSwDelivered === "S" || valSwDelivered === "N") ? valSwDelivered : null;

          //AJ. Calificación:
          const valQualification = element[35];
          if (valQualification == null || valQualification === "") {
            qualification = null;
          } else {
            qualification = Number(valQualification);
            if (isNaN(qualification) || (qualification !== 1 && qualification !== 2 && qualification !== 3 && qualification !== 4 && qualification !== 5)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AJ. La calificación no es válida (1 a 5 o vacío).`;
              this.maintenanceOrdersAlerts.push(alert);
              rowHasError = true;
            }
          }

          //AK. Estado:
          stateMaintenanceOrder = element[36] ? Number(element[36]) : 1;
          if (stateMaintenanceOrder !== MaintenanceOrderEnums.abierta && stateMaintenanceOrder !== MaintenanceOrderEnums.cierreInicial &&
            stateMaintenanceOrder !== MaintenanceOrderEnums.cierreFinal && stateMaintenanceOrder !== MaintenanceOrderEnums.anulada) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AK. El estado del documento no es válido (1, 2, 3, 4).`;
            this.maintenanceOrdersAlerts.push(alert);
            rowHasError = true;
          }

          if (!rowHasError) {
            row = new MaintenanceOrderImportModel(
              counter, this.companyId!, this.branchId, maintenanceOrderClass, maintenanceOrderType,
              maintenanceOrderConsecutive, maintenanceOrderAlterNumber, maintenanceOrderDate, workshopType, clientDocument,
              clientDocument2, costCenterCode, equipmentCode, storeCode, mechanicDocument, maintenanceType,
              initialDate, initialDiagnosis, finalDate, finalDiagnosis, repairHours,
              kmHoromValue, stageCode, reportedBy, receivedBy, contactConstructionSite,
              dateCompromise, transportDocument, vehicleCode, transportValue, transportValueToPay,
              valueToInvoiced, invoiceNumber, swChargeConstructionSite, costRepairValue, swDeliveredOnTime,
              qualification, stateMaintenanceOrder
            );
            rowsImports.push(row);
          } else {
            isOk = false;
          }
        });
        if (isOk && rowsImports.length > 0) {
          try {
            const response = await firstValueFrom(this.maintenanceOrderService.importMaintenanceOrders(rowsImports));
            this.loadPreInvoicingModel = response;
            this.maintenanceOrdersAlerts = response?.alerts ?? [];
            this.errorMaintenanceOrder = response?.preInvoicingStateId;

            if (this.errorMaintenanceOrder === 1) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast(
                'error',
                'La importación terminó con errores, puede exportarlos si lo desea.',
                'ERROR');
            } else if (this.maintenanceOrdersAlerts.length > 0) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast('error', 'La importación terminó con alertas, puede exportarlas si lo desea');
            } else {
              this.visibleTxt = false;
              this.toastHandlerService.showToast('success', `La importación terminó exitosamente.`);
            }
            this.clearInputFile();
          } catch (error) {
            this.nameFile = 'Ningún archivo seleccionado.';
            console.error('Error al importar las ordenes de mantenimiento.', error);
          }
          (<HTMLInputElement>document.getElementById('fileImport')).value = '';
        } else {
          this.errorMaintenanceOrder = 1;
          this.visibleTxt = true;
          this.toastHandlerService.showToast(
            'error',
            'La importación terminó con errores, puede exportarlos si lo desea'
          );
        }
      });
    } else {
      this.toastHandlerService.showToast('error', 'No ha seleccionado ningún archivo.');
    }
  }

  openToggle(event: any) {
    this.informationPopover.toggle(event);
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatoria.',
      },
      branchId: {
        required: 'La sucursal es obligatoria.',
        min: 'La sucursal es obligatoria.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formImport.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
    });
  }

}
